// Libraries
import _ from 'lodash';

// Types
import { TemperatureType, PressureType } from '../types/global';

// Plugins
import { convertTemperature, convertPressure } from './general';

/**
 * Base interface for range configuration
 * @interface RangeConfig
 * @property {number} minValue - Minimum range value
 * @property {number} maxValue - Maximum range value
 */
export interface RangeConfig {
  minValue: number;
  maxValue: number;
}

/**
 * Interface for temperature range configuration
 * @interface TemperatureRangeConfig
 * @extends {RangeConfig}
 * @property {TemperatureType} unit - Temperature unit (°C or °F)
 */
export interface TemperatureRangeConfig extends RangeConfig {
  unit: TemperatureType;
}

/**
 * Interface for pressure range configuration
 * @interface PressureRangeConfig
 * @extends {RangeConfig}
 * @property {PressureType} unit - Pressure unit (bar, kPa or psi)
 */
export interface PressureRangeConfig extends RangeConfig {
  unit: PressureType;
}

/**
 * Returns motor RPM range
 * Default values: minValue = 0 RPM, maxValue = 3000 RPM
 * @function MOTOR_RPM_RANGE
 * @returns Motor RPM range configuration
 */
export const MOTOR_RPM_RANGE: RangeConfig = {
  minValue: 0,
  maxValue: 3000,
};

/**
 * Returns motor amperage range
 * Default values: minValue = 0 A, maxValue = 750 A
 * @function MOTOR_AMPS_RANGE
 * @returns Motor amperage range configuration
 */
export const MOTOR_AMPS_RANGE: RangeConfig = {
  minValue: 0,
  maxValue: 750,
};

/**
 * Returns temperature range based on the provided unit
 * Default values are in Celsius (°C): minValue = -17.7°C, maxValue = 150°C
 * @function TEMPERATURE_RANGE
 * @param {TemperatureType} unit - Desired temperature unit
 * @returns {TemperatureRangeConfig} Temperature range configuration
 */
export const TEMPERATURE_RANGE = (
  unit: TemperatureType,
): TemperatureRangeConfig => {
  const minTemp: number | null | undefined = convertTemperature(-17.7, unit).value;
  const maxTemp: number | null | undefined = convertTemperature(150, unit).value;

  return {
    minValue: _.isNil(minTemp) ? -17.7 : minTemp,
    maxValue: _.isNil(maxTemp) ? 150 : maxTemp,
    unit,
  };
};

/**
 * Returns clamp pressure range based on the provided unit
 * Default values are in MPa: minValue = 0 MPa, maxValue = 25 MPa
 * @function CLAMP_PRESSURE_RANGE
 * @param {PressureType} unit - Desired pressure unit
 * @returns {PressureRangeConfig} Clamp pressure range configuration
 */
export const CLAMP_PRESSURE_RANGE = (unit: PressureType): PressureRangeConfig => {
  const minPressure: number | null | undefined = convertPressure(0, unit).value;
  const maxPressure: number | null | undefined = convertPressure(25, unit).value;

  return {
    minValue: _.isNil(minPressure) ? 0 : minPressure,
    maxValue: _.isNil(maxPressure) ? 25 : maxPressure,
    unit,
  };
};

/**
 * Returns tramp pressure range based on the provided unit
 * Default values are in MPa: minValue = 0 MPa, maxValue = 25 MPa
 * @function TRAMP_PRESSURE_RANGE
 * @param {PressureType} unit - Desired pressure unit
 * @returns {PressureRangeConfig} Tramp pressure range configuration
 */
export const TRAMP_PRESSURE_RANGE = (unit: PressureType): PressureRangeConfig => {
  const minPressure: number | null | undefined = convertPressure(0, unit).value;
  const maxPressure: number | null | undefined = convertPressure(25, unit).value;

  return {
    minValue: _.isNil(minPressure) ? 0 : minPressure,
    maxValue: _.isNil(maxPressure) ? 25 : maxPressure,
    unit,
  };
};

/**
 * Returns cavity level min and max values
 * Default values: minValue = 0%, maxValue = 100%
 * @function CAVITY_LEVEL_RANGE
 * @returns {RangeConfig} Cavity level range configuration
 */
export const CAVITY_LEVEL_RANGE: RangeConfig = {
  minValue: 0,
  maxValue: 100,
};

/**
 * Returns lubricant temperature range based on the provided unit
 * Default values are in Celsius (°C): minValue = -17.7°C, maxValue = 150°C
 * @function LUBE_TEMPERATURE_RANGE
 * @param {TemperatureType} unit - Desired temperature unit
 * @returns {TemperatureRangeConfig} Lubricant temperature range configuration
 */
export const LUBE_TEMPERATURE_RANGE = (unit: TemperatureType): TemperatureRangeConfig => {
  const minTemp: number | null | undefined = convertTemperature(-17.7, unit).value;
  const maxTemp: number | null | undefined = convertTemperature(150, unit).value;

  return {
    minValue: _.isNil(minTemp) ? -17.7 : minTemp,
    maxValue: _.isNil(maxTemp) ? 150 : maxTemp,
    unit,
  };
};

/**
 * Returns lubricant temperature differential range based on the provided unit
 * Default values are in Celsius (°C): minValue = -10°C, maxValue = 50°C
 * @function LUBE_SUPPLY_DIFFERENTIAL_RANGE
 * @param {TemperatureType} unit - Desired temperature unit
 * @returns {TemperatureRangeConfig} Temperature differential range configuration
 */
export const LUBE_SUPPLY_DIFFERENTIAL_RANGE = (
  unit: TemperatureType,
): TemperatureRangeConfig => {
  const minTemp: number | null | undefined = Number(
    convertTemperature(-10, unit, 0, false, true).value,
  );
  const maxTemp: number | null | undefined = convertTemperature(50, unit, 0, false, true).value;

  return {
    minValue: _.isNil(minTemp) ? -10 : minTemp,
    maxValue: _.isNil(maxTemp) ? 50 : maxTemp,
    unit,
  };
};

/**
 * Returns lubrication pressure range based on the provided unit
 * Default values are in MPa: minValue = 0 MPa, maxValue = 1 MPa
 * @function LUBE_PRESSURE_RANGE
 * @param {PressureType} unit - Desired pressure unit
 * @returns {PressureRangeConfig} Lubrication pressure range configuration
 */
export const LUBE_PRESSURE_RANGE = (unit: PressureType): PressureRangeConfig => {
  const minPressure: number | null | undefined = convertPressure(0, unit).value;
  const maxPressure: number | null | undefined = convertPressure(1, unit).value;

  return {
    minValue: _.isNil(minPressure) ? 0 : minPressure,
    maxValue: _.isNil(maxPressure) ? 1 : maxPressure,
    unit,
  };
};

/**
 * Returns lubrication flow range
 * Default values: minValue = 0 GPM, maxValue = 100 GPM
 * @function LUBE_FLOW_RANGE
 * @returns {RangeConfig} Lubrication flow range configuration
 */
export const LUBE_FLOW_RANGE: RangeConfig = {
  minValue: 0,
  maxValue: 100,
};
